// Apply-time rebase for a queued-edits apply bundle: the general per-verb
// drift strategy run against fresh server state just before executeBatch. The
// stored bundle is from proposal time; by apply time the world may have moved.
// Per verb:
//
//   PATCH  — if the entry's domain change is already present in current state
//            (only the origin tag would change), drop it as satisfied; else
//            keep it and re-point its version precondition at the fresh
//            version (rebaseFhirPathPatchBundle — ops are element-keyed, so
//            they re-resolve safely against the newer version).
//   DELETE — if the target is already absent, drop it as satisfied (deleting
//            it again is at best a no-op, at worst a batch error).
//   POST   — kept as-is: a conditional create's `ifNoneExist` predicate is the
//            server-side, race-free guard against duplicate creation, so
//            pre-checking here would only re-introduce the race. (Review-time
//            detection renders "already exists" — see bundle-to-change-sets.)
//   PUT    — kept as-is; no proposal generator emits PUT entries today.
//
// Satisfied entries are returned so the caller can report them — the review's
// end state still matches what the reviewer approved (the effect is already
// present), it just was not this apply that produced it.

import type { Bundle, Resource } from '@medplum/fhirtypes'
import { rebaseFhirPathPatchBundle } from '#src/fhirpath-patch/index.ts'
import { restampProposalBookkeepingTimestamps } from '#src/queued-edits/bookkeeping.ts'
import { patchEntryAlreadyApplied } from '#src/queued-edits/bundle-to-change-sets.ts'
import { entryHandle } from '#src/queued-edits/entry-handle.ts'
import { now } from '#src/utils.ts'

export interface RebaseProposalApplyBundleResult {
  bundle: Bundle
  // Handles of entries dropped because their effect is already present in
  // current state (PATCH no-op / DELETE target absent).
  satisfiedHandles: string[]
}

// Mutates and returns the bundle (callers pass a clone of the stored
// proposal). `current` is the fresh server state for every mutated reference,
// keyed by `ResourceType/id` — a reference absent from the map means the
// resource no longer exists.
export function rebaseProposalApplyBundle(
  bundle: Bundle,
  current: Record<string, Resource>
): RebaseProposalApplyBundleResult {
  const satisfiedHandles: string[] = []
  const kept = (bundle.entry ?? []).filter((entry) => {
    const method = entry.request?.method
    const ref = entry.request?.url?.split('?')[0]
    // Conditional (search-based) urls can't resolve to one current resource —
    // keep the entry and let the server arbitrate.
    const resolvable = Boolean(ref && /^[A-Za-z]+\/[\w-]+$/.test(ref))
    const target = resolvable && ref ? current[ref] : undefined

    const satisfied =
      method === 'PATCH'
        ? Boolean(target && patchEntryAlreadyApplied(entry, target))
        : method === 'DELETE'
          ? resolvable && !target
          : false

    if (satisfied) {
      const handle = entryHandle(entry)
      if (handle) {
        satisfiedHandles.push(handle)
      }
    }
    return !satisfied
  })
  bundle.entry = kept
  // Registered audit-timestamp ops (e.g. Task.lastModified) encode the
  // proposal BUILD time — stale by construction (a queued review can sit for
  // days, and an interim touch even makes the stored value move time
  // backwards). Re-stamp them with the actual apply time.
  const appliedAt = now()
  for (const entry of kept) {
    if (entry.request?.method === 'PATCH') {
      restampProposalBookkeepingTimestamps(entry, appliedAt)
    }
  }
  rebaseFhirPathPatchBundle(bundle, current)
  return { bundle, satisfiedHandles }
}
